import {
  Badge,
  Button,
  Icon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import ChakraUIRenderer from "chakra-ui-markdown-renderer";
import {
  useMaybernAssistantExampleRequestsListSuspense,
  useMaybernAssistantRequestCreate,
} from "gen/endpoints/maybern-assistant/maybern-assistant";
import { AIRequestDataRequest } from "gen/model/aIRequestDataRequest";
import { AIResponseData } from "gen/model/aIResponseData";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineSparkles } from "react-icons/hi";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { useToast } from "shared/components/toast/useToast";

export const MaybernAssistantModal: React.FC<{
  fundFamilyId: string;
  onCloseComplete: () => void;
}> = ({ fundFamilyId, onCloseComplete }) => {
  const { t } = useTranslation(["ai", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const [assistantResponse, setAssistantResponse] = useState<
    AIResponseData | undefined
  >(undefined);
  const { showErrorToast } = useToast();
  const cancelRef = useRef(null);
  const { data: exampleRequests } =
    useMaybernAssistantExampleRequestsListSuspense(fundFamilyId);
  const { mutateAsync: createMaybernAssistantRequest } =
    useMaybernAssistantRequestCreate();

  const form = useForm();
  const onSubmit = async (data: AIRequestDataRequest) => {
    try {
      setAssistantResponse(undefined);
      const resp = await createMaybernAssistantRequest({
        fundFamilyId,
        data,
      });
      setAssistantResponse(resp);
    } catch (err) {
      showErrorToast(err);
    }
  };

  const submitRandomExample = async () => {
    const randomExample =
      exampleRequests[Math.floor(Math.random() * exampleRequests.length)];
    form.setValue("request", randomExample.request);
    await form.handleSubmit(onSubmit)();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text display="inline-block" mr={2}>
            {t("ai:assistant.name")}
          </Text>
          <Badge colorScheme="teal">{t("common:beta")}</Badge>
        </ModalHeader>
        <Form form={form} onSubmit={onSubmit}>
          <ModalBody>
            <Text mb={4}>{t("ai:assistant.description")}</Text>
            <FormTextArea name="request" title={t("ai:assistant.example")} />
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={ChakraUIRenderer()}
              skipHtml={true}
            >
              {assistantResponse?.response || ""}
            </Markdown>
            <ContentBlockGroup defaultExpanded={false} mt="12px">
              <ContentBlock>
                <ContentBlockHeader>
                  <ContentBlockTitle>{t("ai:examples")}</ContentBlockTitle>
                </ContentBlockHeader>
                <ContentBlockBody>
                  <UnorderedList>
                    {exampleRequests.map((req) => (
                      <ListItem key={req.request}>{req.request}</ListItem>
                    ))}
                  </UnorderedList>
                </ContentBlockBody>
              </ContentBlock>
            </ContentBlockGroup>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button colorScheme="secondary" onClick={submitRandomExample}>
              {t("ai:randomExample")}
            </Button>

            <FormSubmitButton>
              <Icon
                height="16px"
                width="16px"
                as={HiOutlineSparkles}
                color="gray.100"
                mr="5px"
              />
              {t("ai:assistant.generate")}
            </FormSubmitButton>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
